<script lang="ts">
  import { api } from '$shared/api';
  import { trackSettingsChange } from '$lib/analytics.svelte';
  import { isSoundEnabled, setSoundEnabled } from '$shared/toasts.svelte';
  import LoadingSkeleton from '$shared/LoadingSkeleton.svelte';

  let gameSounds = $state(isSoundEnabled());

  let emailNewFollower = $state(true);
  let emailVoteMilestone = $state(true);
  let emailWeeklyRecap = $state(true);
  let loading = $state(true);
  let saving = $state(false);
  let message = $state('');

  interface NotificationSettings {
    email_notify_new_follower: boolean;
    email_notify_vote_milestone: boolean;
    email_notify_weekly_recap: boolean;
  }

  $effect(() => {
    api
      .get<NotificationSettings>('/users/current/notification-settings')
      .then((d) => {
        emailNewFollower = d.email_notify_new_follower;
        emailVoteMilestone = d.email_notify_vote_milestone;
        emailWeeklyRecap = d.email_notify_weekly_recap;
        loading = false;
      })
      .catch((e: unknown) => {
        console.error('Failed to load notification settings:', e);
        loading = false;
      });
  });

  async function saveEmailPrefs() {
    await save({
      email_notify_new_follower: emailNewFollower,
      email_notify_vote_milestone: emailVoteMilestone,
      email_notify_weekly_recap: emailWeeklyRecap,
    });
  }

  async function save(body: Record<string, unknown>) {
    saving = true;
    message = '';
    try {
      await api.put('/users/current/notification-settings', body);
      trackSettingsChange('notifications');
      message = 'Saved';
    } catch (e) {
      console.error('Failed to save notification settings:', e);
      message = 'Failed';
    }
    saving = false;
    setTimeout(() => {
      message = '';
    }, 3000);
  }
</script>

<div class="narrow">
  <h1>Notification Settings</h1>
  {#if loading}
    <LoadingSkeleton lines={5} />
  {:else}
    {#if message}<p class="msg--success">{message}</p>{/if}

    <section class="card card--no-hover section">
      <h2>Email Notifications</h2>
      <label class="check-row">
        <input type="checkbox" bind:checked={emailNewFollower} />
        <span>New follower</span>
      </label>
      <label class="check-row">
        <input type="checkbox" bind:checked={emailVoteMilestone} />
        <span>Vote milestone (5, 10, 25, 50, 100)</span>
      </label>
      <label class="check-row">
        <input type="checkbox" bind:checked={emailWeeklyRecap} />
        <span>Weekly recap (your stats from the past 7 days)</span>
      </label>
      <button class="btn btn-primary" onclick={saveEmailPrefs} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button
      >
    </section>

    <section class="card card--no-hover section">
      <h2>Game Sounds</h2>
      <p class="help">Play sound effects when you earn points, complete games, and receive notifications.</p>
      <label class="check-row">
        <input type="checkbox" bind:checked={gameSounds} onchange={() => setSoundEnabled(gameSounds)} />
        <span>Enable game sounds</span>
      </label>
    </section>
  {/if}
</div>

<style>
  .narrow {
    max-width: 40rem;
    margin: 0 auto;
  }
  h1 {
    margin-bottom: 1.5rem;
  }
  h2 {
    margin: 0 0 0.75rem;
    font-size: 1rem;
  }
  .section {
    padding: 1rem;
    margin-bottom: 1.5rem;
  }
  .help {
    font-size: 0.875rem;
    color: var(--text-secondary);
    margin-bottom: 0.75rem;
  }
  .check-row {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.75rem;
    cursor: pointer;
    font-size: 0.875rem;
  }
  .check-row input {
    accent-color: var(--cyan);
  }
</style>
